import { useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
    describeShippingProgress,
    listShippingProgress,
} from '../../utils/shippingBehavior';

const withShippingEmoji = (message) => `🚚 ${message}`;

const entryReached = (entry) => Boolean(entry?.granted) || Number(entry?.remaining) <= 0;

const ShippingBehaviorBanner = ({
    companyRuc = null,
    policy = null,
    companyName = '',
    className = '',
    compact = false,
    prominent = false,
}) => {
    const activeOrder = useSelector((state) => state.cart?.activeOrder);
    const companiesByRuc = useSelector((state) => state.companies?.companiesByRuc || {});

    const entries = listShippingProgress({
        activeOrder,
        companiesByRuc,
        companyRuc,
        policy,
        companyName,
    });

    const reachedIds = entries.filter(entryReached).map((entry) => entry.key).join('|');
    const celebratedRef = useRef(new Set());
    const [celebrating, setCelebrating] = useState([]);

    useEffect(() => {
        const ids = new Set(reachedIds ? reachedIds.split('|') : []);
        const celebrated = celebratedRef.current;

        [...celebrated].forEach((id) => {
            if (!ids.has(id)) celebrated.delete(id);
        });

        const fresh = [...ids].filter((id) => !celebrated.has(id));
        fresh.forEach((id) => celebrated.add(id));

        setCelebrating((current) => {
            const stillReached = current.filter((id) => ids.has(id));
            if (fresh.length === 0 && stillReached.length === current.length) return current;
            return [...new Set([...stillReached, ...fresh])];
        });

        if (fresh.length === 0) return undefined;

        let finished = false;
        const timer = window.setTimeout(() => {
            finished = true;
            setCelebrating((current) => current.filter((id) => !fresh.includes(id)));
        }, 700);

        return () => {
            window.clearTimeout(timer);
            // Strict Mode clears this timer before it fires. Drop the keys so the
            // second run can start the pulse again.
            if (!finished) {
                fresh.forEach((id) => celebrated.delete(id));
            }
        };
    }, [reachedIds]);

    if (entries.length === 0) {
        return null;
    }

    const allReached = entries.every(entryReached);
    const densityClass = prominent
        ? 'home-promo-banner mb-0'
        : compact
            ? `promo-notice ${className || 'mb-2'}`
            : `py-2 px-3 ${className || 'mb-3'}`;

    return (
        <Alert className={`shipping-behavior-banner ${densityClass}${allReached ? ' is-reached' : ''}`}>
            {entries.map((entry) => {
                const copy = describeShippingProgress({
                    ...entry,
                    companyName: entry.name,
                    includeCompanyName: Boolean(entry.name),
                });
                const reached = entryReached(entry);
                const message = withShippingEmoji(copy.message);
                const entryClass = [
                    'shipping-behavior-entry',
                    prominent ? 'home-promo-row' : '',
                    reached ? 'is-reached' : '',
                    celebrating.includes(entry.key) ? 'is-celebrate' : '',
                ].filter(Boolean).join(' ');
                const meter = (
                    <div
                        className={prominent ? 'home-promo-meter' : 'shipping-behavior-progress'}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={copy.progress}
                        aria-label="Avance hacia el envío gratis"
                    >
                        <span style={{ width: `${copy.progress}%` }} />
                    </div>
                );

                if (prominent) {
                    return (
                        <div key={entry.key} className={entryClass}>
                            <div className="home-promo-copy shipping-behavior-message fw-semibold" aria-live="polite">
                                {message}
                            </div>
                            {meter}
                        </div>
                    );
                }

                return (
                    <div key={entry.key} className={entryClass}>
                        {compact ? (
                            <div className="shipping-behavior-message fw-semibold" aria-live="polite">
                                {message}
                            </div>
                        ) : (
                            <>
                                <div className="shipping-behavior-message fw-semibold">{copy.title}</div>
                                <div className="small mt-1" aria-live="polite">{message}</div>
                            </>
                        )}
                        {meter}
                    </div>
                );
            })}
        </Alert>
    );
};

export default ShippingBehaviorBanner;
